/**
 * Learning Pathways Page
 *
 * Displays available learning pathways and user progress
 * Phase 2 of the MEDD-SIM Architecture Plan
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2,
  GraduationCap,
  Target,
  CheckCircle,
  Lock,
  Play,
  Trophy,
  Clock,
  ArrowRight,
  BookOpen,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import { useEnrollInPathway } from '@/hooks/useLearningLoop';

interface LearningPathway {
  id: string;
  name: string;
  description: string;
  category: string;
  difficulty_level: string;
  certification_name: string | null;
  estimated_duration_hours: number | null;
  is_active: boolean;
  moduleCount?: number;
  thumbnail_url?: string | null;
}

interface UserEnrollment {
  id: string;
  pathway_id: string;
  status: string;
  modules_completed: number;
  total_score: number;
  enrolled_at: string;
  completed_at: string | null;
  learning_pathways: LearningPathway;
}

export default function LearningPathways() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const currentTenantId = tenant.tenant_id;
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('available');

  const enrollMutation = useEnrollInPathway();

  // Fetch available pathways - TENANT ISOLATED
  // Users see: system templates (created by MEDD) + their tenant's pathways + their company's pathways
  const { data: pathways, isLoading: pathwaysLoading, error: pathwaysError } = useQuery({
    queryKey: ['learning-pathways', profile?.company_id, currentTenantId],
    queryFn: async () => {
      const companyId = profile?.company_id;
      
      let query = supabase
        .from('learning_pathways')
        .select(`
          *,
          pathway_modules (id)
        `)
        .eq('is_active', true);

      // TENANT ISOLATION: 
      // - MEDD system templates (created_by_master = true AND is_system_template = true) visible to all
      // - Tenant-specific pathways only visible within that tenant
      // - Pathways assigned to specific tenants (assigned_tenants contains current tenant)
      // - Company pathways only visible to that company
      
      // FIX: Only show SYSTEM TEMPLATES globally, not all master-created pathways
      // created_by_master alone shouldn't grant visibility - must also be a system template
      const filters = ['and(created_by_master.eq.true,is_system_template.eq.true)']; // Only MEDD system templates visible to all
      
      if (currentTenantId) {
        // Add tenant-specific pathways (pathways created within this tenant)
        filters.push(`tenant_id.eq.${currentTenantId}`);
        // Add pathways specifically assigned to this tenant
        filters.push(`assigned_tenants.cs.{${currentTenantId}}`);
      }
      
      // Apply tenant filter first
      query = query.or(filters.join(','));
      
      // Then apply company/visibility filter within tenant scope
      if (companyId) {
        // Within visible pathways, show: public OR user's company OR assigned to all OR assigned to user's company
        query = query.or(
          `is_public.eq.true,` +
          `company_id.eq.${companyId},` +
          `assign_to.eq.all,` +
          `is_system_template.eq.true,` +
          `and(assign_to.eq.companies,assigned_companies.cs.{${companyId}})`
        );
      } else {
        // No company - only show public pathways and system templates
        query = query.or('is_public.eq.true,is_system_template.eq.true');
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map(p => ({
        ...p,
        moduleCount: p.pathway_modules?.length || 0
      })) as LearningPathway[];
    },
    enabled: !!profile?.id
  });

  // Fetch user enrollments
  const { data: enrollments, isLoading: enrollmentsLoading } = useQuery({
    queryKey: ['user-enrollments', profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_pathway_enrollments')
        .select(`
          *,
          learning_pathways (*)
        `)
        .eq('user_id', profile?.id)
        .order('enrolled_at', { ascending: false });

      if (error) throw error;
      return data as UserEnrollment[];
    },
    enabled: !!profile?.id
  });

  const handleEnroll = async (pathwayId: string) => {
    try {
      await enrollMutation.mutateAsync(pathwayId);
      toast.success('Successfully enrolled in pathway!');
      setActiveTab('my-pathways');
    } catch (error: any) {
      toast.error(error.message || 'Failed to enroll');
    }
  };

  const isEnrolled = (pathwayId: string) => {
    return enrollments?.some(e => e.pathway_id === pathwayId);
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'intermediate': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'advanced': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'expert': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500">In Progress</Badge>;
      case 'enrolled':
        return <Badge variant="outline">Not Started</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (pathwaysLoading || enrollmentsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeEnrollments = enrollments?.filter(e => e.status !== 'completed') || [];
  const completedEnrollments = enrollments?.filter(e => e.status === 'completed') || [];
  const unenrolledPathways = pathways?.filter(p => !isEnrolled(p.id)) || [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <GraduationCap className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Learning Pathways</h1>
        </div>
        <p className="text-muted-foreground">
          Structured learning programs to master your skills and earn certifications
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{activeEnrollments.length}</div>
              <div className="text-xs text-muted-foreground">Active Pathways</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Trophy className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{completedEnrollments.length}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Award className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{completedEnrollments.length}</div>
              <div className="text-xs text-muted-foreground">Certifications</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Target className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{unenrolledPathways.length}</div>
              <div className="text-xs text-muted-foreground">Available</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="my-pathways">
            My Pathways
            {activeEnrollments.length > 0 && (
              <Badge variant="secondary" className="ml-2">{activeEnrollments.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="available">
            Available
            {unenrolledPathways.length > 0 && (
              <Badge variant="secondary" className="ml-2">{unenrolledPathways.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed
            {completedEnrollments.length > 0 && (
              <Badge variant="secondary" className="ml-2">{completedEnrollments.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* My Pathways Tab */}
        <TabsContent value="my-pathways">
          {activeEnrollments.length === 0 ? (
            <Card className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Active Pathways</h3>
              <p className="text-muted-foreground mb-4">
                Start your learning journey by enrolling in a pathway
              </p>
              <Button onClick={() => setActiveTab('available')}>
                Browse Pathways
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeEnrollments.map((enrollment) => (
                <PathwayProgressCard
                  key={enrollment.id}
                  enrollment={enrollment}
                  onContinue={() => navigate(`/learning/pathway/${enrollment.pathway_id}`)}
                  moduleCount={(enrollment.learning_pathways as any)?.moduleCount || 0}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Available Pathways Tab */}
        <TabsContent value="available">
          {unenrolledPathways.length === 0 ? (
            <Card className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
              <p className="text-muted-foreground">
                You're enrolled in all available pathways. Check back later for new content.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {unenrolledPathways.map((pathway) => (
                <PathwayCard
                  key={pathway.id}
                  pathway={pathway}
                  onEnroll={() => handleEnroll(pathway.id)}
                  isEnrolling={enrollMutation.isPending}
                  getDifficultyColor={getDifficultyColor}
                  onViewDetails={() => navigate(`/learning/pathway/${pathway.id}`)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Completed Pathways Tab */}
        <TabsContent value="completed">
          {completedEnrollments.length === 0 ? (
            <Card className="p-12 text-center">
              <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Completed Pathways Yet</h3>
              <p className="text-muted-foreground mb-4">
                Complete your first pathway to earn a certification
              </p>
              <Button onClick={() => setActiveTab('my-pathways')}>
                View Active Pathways
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {completedEnrollments.map((enrollment) => (
                <CompletedPathwayCard
                  key={enrollment.id}
                  enrollment={enrollment}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Pathway Card Component
function PathwayCard({
  pathway,
  onEnroll,
  isEnrolling,
  getDifficultyColor,
  onViewDetails
}: {
  pathway: LearningPathway;
  onEnroll: () => void;
  isEnrolling: boolean;
  getDifficultyColor: (level: string) => string;
  onViewDetails?: () => void;
}) {
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group border-border/50" onClick={onViewDetails}>
      {/* Featured Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-primary/10 to-primary/5 overflow-hidden">
        {pathway.thumbnail_url ? (
          <img
            src={pathway.thumbnail_url}
            alt={pathway.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <GraduationCap className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Badges - Top */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <Badge variant="outline" className="capitalize text-[10px] bg-background/80 backdrop-blur-sm shadow-lg">{pathway.category}</Badge>
          <Badge className={`text-[10px] shadow-lg border-0 ${getDifficultyColor(pathway.difficulty_level)}`}>
            {pathway.difficulty_level}
          </Badge>
        </div>
        
        {/* Title - Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-semibold text-base text-white truncate drop-shadow-lg">{pathway.name}</h3>
        </div>
      </div>
      
      {/* Card Body */}
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[32px]">
          {pathway.description}
        </p>

        <div className="flex items-center gap-4 text-[10px] text-muted-foreground mb-3">
          <span className="flex items-center gap-1">
            <BookOpen className="w-3 h-3" />
            {pathway.moduleCount || 0} modules
          </span>
          {pathway.estimated_duration_hours && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              ~{pathway.estimated_duration_hours}h
            </span>
          )}
        </div>

        {pathway.certification_name && (
          <Badge variant="secondary" className="flex items-center gap-1 text-[10px] w-fit mb-3">
            <Award className="w-3 h-3" />
            Certification
          </Badge>
        )}

        <Button
          className="w-full text-xs h-8"
          onClick={(e) => { e.stopPropagation(); onViewDetails ? onViewDetails() : onEnroll(); }}
          disabled={isEnrolling}
        >
          {isEnrolling ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <>
              View Pathway
              <ArrowRight className="w-3 h-3 ml-1.5" />
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// Progress Card Component
function PathwayProgressCard({
  enrollment,
  onContinue,
  moduleCount = 0
}: {
  enrollment: UserEnrollment;
  onContinue: () => void;
  moduleCount?: number;
}) {
  const pathway = enrollment.learning_pathways;
  const totalModules = moduleCount || (pathway as any).moduleCount || 0;
  const progress = totalModules > 0
    ? (enrollment.modules_completed / totalModules) * 100
    : 0;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group border-border/50" onClick={onContinue}>
      {/* Featured Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-primary/10 to-primary/5 overflow-hidden">
        {(pathway as any).thumbnail_url ? (
          <img
            src={(pathway as any).thumbnail_url}
            alt={pathway.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <GraduationCap className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Status Badge - Top */}
        <div className="absolute top-3 left-3">
          {enrollment.status === 'in_progress' && (
            <Badge className="bg-blue-600 text-white text-[10px] shadow-lg border-0">In Progress</Badge>
          )}
        </div>
        
        {/* Title - Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-semibold text-base text-white truncate drop-shadow-lg">{pathway.name}</h3>
        </div>
      </div>
      
      {/* Card Body */}
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3 min-h-[32px]">{pathway.description}</p>
        
        {/* Progress Stats */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] text-muted-foreground">
            <span className="font-semibold text-foreground text-sm">{enrollment.modules_completed}</span>/{totalModules} modules
          </div>
          <div className="text-[10px] text-muted-foreground">
            <span className="font-semibold text-primary text-sm">{Math.round(progress)}%</span>
          </div>
        </div>
        
        <Progress value={progress} className="h-1.5 mb-3" />

        {enrollment.total_score > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-3">
            <Target className="w-3 h-3" />
            Avg Score: <span className="font-medium text-foreground">{enrollment.total_score}</span>
          </div>
        )}

        <Button onClick={(e) => { e.stopPropagation(); onContinue(); }} className="w-full text-xs h-8">
          <Play className="w-3 h-3 mr-1.5" />
          Continue Learning
        </Button>
      </CardContent>
    </Card>
  );
}

// Completed Pathway Card
function CompletedPathwayCard({ enrollment }: { enrollment: UserEnrollment }) {
  const pathway = enrollment.learning_pathways;

  return (
    <Card className="p-6 bg-gradient-to-br from-green-50 to-transparent dark:from-green-950/20 border-green-200 dark:border-green-800">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-green-100 dark:bg-green-900/40 rounded-full">
          <Trophy className="w-6 h-6 text-green-600 dark:text-green-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg mb-1">{pathway.name}</h3>
          {pathway.certification_name && (
            <Badge className="bg-green-500 mb-2">
              <Award className="w-3 h-3 mr-1" />
              {pathway.certification_name}
            </Badge>
          )}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Completed {new Date(enrollment.completed_at!).toLocaleDateString()}</span>
            <span>Score: {enrollment.total_score}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
